<script lang="ts" setup>
import { computed, onUnmounted, ref } from 'vue'
import type { HomePageConfig } from '../configs/types'
import { xiaoyaoConfig } from '../configs/common'
import ModuleBanner from './modules/ModuleBanner.vue'
import ModuleSlideBook from './modules/ModuleSlideBook.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleFeedTalewave from './modules/ModuleFeed.talewave.vue'
import ModuleTags2x3 from './modules/ModuleTags2x3.vue'
import ModuleRanking from './modules/ModuleRanking.vue'
import ModuleSwiper from './modules/ModuleSwiper.vue'
import ModuleBookSwiper from './modules/ModuleBookSwiper.vue'
import { bannerShow } from '@/composables/useBannerShow'
import { COLUMN_TYPE } from '@/constants'
import xiaoyaoIcon from '@/assets/xiaoyao.png'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()

// 声明构建时注入的品牌常量（用于判断 talewave）
declare const __BRAND__: 'rocnovel' | 'oceread' | 'talewave' | 'ficmoon' | 'novelarc' | 'storyella' | 'florastory' | 'silktale' | 'pearla' | 'fictora' | 'marinovel' | 'flipnovel' | 'moonpage' | 'storlyn' | 'novaris'

const isTalewave = __BRAND__ === 'talewave'

const progressGradient = computed(() => props.config.theme.tabActiveGradient)
const tabActiveTextColor = computed(() => props.config.theme.tabActiveColor || 'var(--un-preset-theme-colors-n-text-1)')
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg || 'var(--un-preset-theme-colors-n-bg-global, #fff)')
const onBannerActiveColor = computed(() => props.config.theme.tabActiveColor || '#53A2FF')

const showXiaoyaoIcon = computed(() => {
  const currentHost = window.location.host
  return xiaoyaoConfig.allowedDomains.includes(currentHost)
})

function handleInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}

// 顶部 Banner: 仅当 modules[0].columnType === BANNER 时生效
const bannerModule = computed(() => {
  const first = props.modules[0]
  return first?.columnType === COLUMN_TYPE.BANNER ? first : null
})
const restModules = computed(() => bannerModule.value ? props.modules.slice(1) : props.modules)
const hasBanner = computed(() => !!bannerModule.value)

// 滚动监听 + sticky-header 透明态切换
const scrollTop = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)
const showHeaderBg = computed(() => {
  if (!hasBanner.value)
    return true
  return scrollTop.value > 60
})

function handleScroll(e: Event) {
  const target = e.target as HTMLElement
  scrollTop.value = target.scrollTop
}

function setScrollRef(el: HTMLElement | null) {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  scrollContainer.value = el
  if (el)
    el.addEventListener('scroll', handleScroll, { passive: true })
  props.setInfiniteRef(el)
}

onUnmounted(() => {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
})
</script>

<template>
  <div :ref="setScrollRef" class="h-100dvh overflow-y-scroll relative">
    <!-- Sticky header: Tab + Search 浮在顶部 -->
    <div class="sticky-header" :class="{ 'show-bg': showHeaderBg || inputFocused, 'on-banner': hasBanner && !showHeaderBg && !inputFocused }">
      <!-- Tab -->
      <div v-show="!inputFocused" class="py-2.5 overflow-x-auto px-15px">
        <div class="flex items-center space-x-30px">
          <img v-if="showXiaoyaoIcon" :src="xiaoyaoIcon" alt="xiaoyao" class="h-24px flex-shrink-0">
          <div v-for="item in pageList" :key="item.id">
            <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
              <span class="text-14px whitespace-nowrap">{{ item.showName }}</span>
              <i v-if="config.theme.showTabUnderline" class="tab-underline" />
            </div>
          </div>
        </div>
      </div>
      <!-- Search -->
      <div class="px-15px pb-2.5 transition-all" :class="{ 'w-full!': inputFocused }">
        <div class="flex items-center space-x-5 relative z-10">
          <i-assets-icon-search class="search-icon w-4 h-4 absolute left-3 top-2.5" />
          <input
            :ref="setInputRef" :value="input" enterkeyhint="search"
            class="search-input !ml0 flex-1 h-9 w-full pl-9 pr-4 rounded-full border-0 !outline-0 truncate caret-n-primary placeholder:text-14px"
            :placeholder="config.texts.searchPlaceholder" @input="handleInput" @keyup.enter="onSearch"
          >
        </div>
        <div v-if="inputFocused" class="fixed inset-0 w-full h-100vh z-9 bg-n-bg-card" />
      </div>
    </div>

    <!-- 顶部 Banner: 大背景，向上偏移延伸到 sticky-header 下方 -->
    <div v-if="bannerModule" v-show="!inputFocused" class="banner-wrapper">
      <ModuleBanner
        :module="bannerModule"
        :config="config"
        position="top"
        :goto-book="gotoBook"
      />
    </div>

    <div class="home-bg px-15px pb-20" :class="{ 'with-banner': hasBanner }">
      <!-- Modules -->
      <div v-if="skeleton" class="h-100dvh">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center font-bold">
          {{ config.texts.loading }}
        </p>
      </div>
      <div v-else v-show="!inputFocused">
        <div v-for="(col, index) in restModules" :key="col.id" :class="{ 'mt-3.75': index > 0 || !hasBanner }">
          <!-- 非顶部 Banner -->
          <template v-if="col.columnType === COLUMN_TYPE.BANNER">
            <ModuleBanner
              :module="col"
              :config="config"
              position="inline"
              :goto-book="gotoBook"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
            <ModuleSlideBook
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <!-- 新增2行3列的模块 -->
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <ModuleTags2x3
              :module="col"
              :config="config"
              :current-tag-id="currentTagId"
              :goto-book="gotoBook"
              :goto-tags="gotoTags"
              :get-current-tag-resources="getCurrentTagResources"
              :get-current-show-tags="getCurrentShowTags"
              :handle-tag-click="handleTagClick"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <ModuleFeatured1Plus4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <ModuleGrid2x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <ModuleGrid1x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <ModuleRanking :module="col" :config="config" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <component :is="isTalewave ? ModuleBookSwiper : ModuleSwiper" :module="col" :config="config" class="my-4 w-full" />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
        </div>
      </div>

      <!-- Feed -->
      <component
        :is="isTalewave ? ModuleFeedTalewave : ModuleFeed"
        v-if="feeds.length"
        :key="pageInfo.id"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar v-show="!inputFocused" />
  </div>
</template>

<style lang="scss" scoped>
/* Sticky header */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: transparent;
  transition: background 0.3s ease;
}

.sticky-header.show-bg {
  background: v-bind(stickyHeaderBg);
}

.sticky-header.show-bg .home-tab.active span {
  color: v-bind(onBannerActiveColor);
}

.sticky-header.show-bg .home-tab.active .tab-underline {
  background: v-bind(onBannerActiveColor);
}

/* Tab */
.home-tab {
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
}

.home-tab span {
  color: var(--un-preset-theme-colors-n-text-3);
  transition: color 0.3s ease;
}

.home-tab.active span {
  position: relative;
  line-height: 20px;
  font-size: 20px;
  color: v-bind(tabActiveTextColor);
  font-weight: bold;
  z-index: 1;
}

.home-tab.active i {
  position: absolute;
  left: 0;
  bottom: 0;
  margin-bottom: 2px;
  width: 100%;
  height: 6px;
  background-image: v-bind(progressGradient);
  border-radius: 3px;
  z-index: 0;
}

.home-tab.active .tab-underline {
  display: block;
  position: static;
  width: calc(100% - 18px);
  max-width: 50px;
  height: 4px;
  background: v-bind(tabActiveTextColor);
  border-radius: 3px;
  margin-top: 4px;
}

/* Search */
.search-input {
  background: var(--un-preset-theme-colors-n-bg-card);
  color: var(--un-preset-theme-colors-n-text-1);
  transition: background 0.3s ease, color 0.3s ease;
}

.sticky-header.show-bg .search-input {
  background: #ffffff;
}

.search-icon {
  color: var(--un-preset-theme-colors-n-text-2);
  transition: color 0.3s ease;
}

/* on-banner: 浅色文字 + 半透明搜索框 */
.sticky-header.on-banner .home-tab span {
  color: rgba(255, 255, 255, 0.85);
}

.sticky-header.on-banner .home-tab.active span {
  color: v-bind(onBannerActiveColor);
}

.sticky-header.on-banner .home-tab.active .tab-underline {
  background: v-bind(onBannerActiveColor);
}

.sticky-header.on-banner .search-input {
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
}

.sticky-header.on-banner .search-input::placeholder {
  color: rgba(255, 255, 255, 0.7);
}

.sticky-header.on-banner .search-icon {
  color: rgba(255, 255, 255, 0.8);
}

/* Banner-wrapper: 向上偏移，让 banner 延伸到 sticky-header 下方
   留足 116px 余量与 banner-top-mask 高度对齐，确保 banner 顶部不会在 sticky-header 上方露出 */
.banner-wrapper {
  position: relative;
  margin-top: -116px;
  z-index: 0;
}

/* home-bg */
.home-bg {
  position: relative;
  z-index: 1;
}

.home-bg.with-banner {
  margin-top: -36px;
  z-index: 2;
}
</style>
